import type { ArtifactV1Data, CaseV1Data, CharacterV0Data } from "@dd/schema";
import { useStore } from "zustand";
import { useGameStore } from "../state/gameStore";
import { artifactNav } from "./artifactNav";

/**
 * Hooks de conveniencia para las apps: caso cargado, artefactos por app,
 * personajes por id y selección de artefacto por app.
 */

export function useKase(): CaseV1Data {
  const kase = useGameStore((s) => s.kase);
  if (!kase) throw new Error("No hay caso cargado — la Shell debe cargarlo antes del Desktop");
  return kase;
}

/** Artefactos VISIBLES de una app (F1-T03: bloqueados y removidos no aparecen) */
export function useAppArtifacts(appId: string): ArtifactV1Data[] {
  const kase = useKase();
  const visible = useGameStore((s) => s.derived?.visibleArtifacts);
  return kase.artifacts.filter((a) => a.app === appId && (visible?.has(a.id) ?? true));
}

/**
 * Huecos diegéticos (F1-T03): artefactos que el jugador VIO y un evento
 * scriptado removió — la ausencia como pista (jugabilidad §3).
 */
export function useRemovedArtifacts(appId: string): ArtifactV1Data[] {
  const kase = useKase();
  const derived = useGameStore((s) => s.derived);
  if (!derived) return [];
  return kase.artifacts.filter(
    (a) =>
      a.app === appId && derived.removedArtifacts.has(a.id) && derived.viewedArtifacts.has(a.id),
  );
}

export function useCharacter(id: string | undefined): CharacterV0Data | undefined {
  const kase = useKase();
  return id ? kase.cast.find((c) => c.id === id) : undefined;
}

export function useSelectedArtifact(appId: string): ArtifactV1Data | null {
  const kase = useKase();
  const selectedId = useStore(artifactNav, (s) => s.selected[appId] ?? null);
  return selectedId ? (kase.artifacts.find((a) => a.id === selectedId) ?? null) : null;
}
